import { formatProductSize } from '~/utils/format-product-size';
import { json, LoaderFunction, redirect } from '@remix-run/node';
import { useLoaderData, useNavigate, Link, useSearchParams } from '@remix-run/react';
import type { MetaFunction } from '@remix-run/node';
import type { Product } from '~/api/product-data.server';
import { getProductBySlug } from '~/api/product-data.server';
import { authFromRequest } from '~/utils/session.server';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useCart } from '~/context/cart-context';
import { toast } from 'react-hot-toast';
import {
  getFillerImage,
  isLightBrandLogo,
  LIGHT_LOGO_TILE,
  prioritizeProductImages,
} from '~/utils/get-filler-image';
import { isVapeProduct, DisposalNotice } from '~/utils/vape-disposal';
import { useBrandLogoCache } from '~/context/brand-logo-context';
import { analytics } from '~/utils/analytics';
import { parse } from 'cookie';
import SuggestedProductsSlider from '~/components/product-modal/suggested-product-slider';
import ShopNearbyDispensaries from '~/components/shop-nearby-dispensaries';
import FaqsSection from '~/routes/_layout.dispensary-locations.$location/components/faqs-section';
import LowStockAlert from '~/components/low-stock-alert';
import { useStickyOptimized } from '~/hooks/use-sticky-optimized';
import ProductReviewModal from '~/components/product-review-modal';
import ProductReviews from '~/components/product-reviews';
import ProductVariants from '~/components/product-modal/product-variants';
import { useReviewCache } from '~/context/review-context';
import { parseCartError, isErrorAlreadyHandled } from '~/utils/cart-error-handler';
import { useUserReview } from '~/hooks/use-user-review';
import ProductTaxonomyLinks from '~/components/product-taxonomy-links';
import { categoryPath } from '~/utils/category-slug';
import {
  getProductAttributes,
  recoverBrandFromSlug,
  resolveOutOfStockTarget,
} from '~/utils/product-fallback.server';

// WordPress location interface - from shared utility
import { fetchAllLocations, type WordPressLocation } from '~/utils/locations.server';
import { socialImageMeta } from '~/utils/social-meta';

import { cartFetch } from '~/utils/cart-queue';
interface Review {
  id: string;
  user_id: string;
  product_id: string;
  rating: number;
  comment: string;
  created: string;
  status: string;
  user: {
    created_at: string;
    email: string;
    raw_app_meta_data: string;
  };
}

interface LoaderData {
  product: Product;
  defaultAvailable: boolean;
  store: string;
  allLocations: WordPressLocation[];
  currentStoreLocation: WordPressLocation | null;
  productUrl: string;
}

/**
 * "HONEY RUNTZ 14G" -> "Honey Runtz 14G". Typesense stores product names
 * upper-cased; every place that shows one to a human title-cases it first, and
 * this used to be four copies of the same expression.
 */
function toTitleCase(value: string | null | undefined): string {
  if (!value) return '';
  return value
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/** Cut to `max`, on a word boundary where one is close enough, with an ellipsis. */
function truncateOnWord(value: string, max: number): string {
  if (value.length <= max) return value;
  const cut = value.slice(0, max - 1);
  const space = cut.lastIndexOf(' ');
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).replace(/[\s.,;:!?-]+$/, '')}…`;
}

/**
 * Cut to `max`, preferring to end on a sentence so the snippet reads as a
 * finished thought rather than a clipped one. Falls back to a word boundary.
 */
function truncateToSentence(value: string, max: number): string {
  if (value.length <= max) return value;
  const cut = value.slice(0, max - 1);
  const stop = Math.max(cut.lastIndexOf('. '), cut.lastIndexOf('! '), cut.lastIndexOf('? '));
  if (stop > max * 0.65) return cut.slice(0, stop + 1);
  return truncateOnWord(value, max);
}

export const meta: MetaFunction = ({ data, params, location }) => {
  const { product } = (data ?? {}) as LoaderData;
  const siteName = 'Catalyst Cannabis';
  const productName = toTitleCase(product?.name) || params.slug || '';
  const brand = product?.brand;

  // Longest title that fits 60 characters, dropping the least valuable part
  // first. Measured over all 1,169 in-stock products at Bellflower: 73% keep the
  // site name, 93% keep the brand, 1% need the name truncated, none overrun.
  //
  // The product name is never dropped, only shortened as a last resort. It is
  // the sole part that distinguishes one product page from another — dropping it
  // would give every product from a brand the same title.
  //
  // "Catalyst" rather than "Catalyst Cannabis" is worth 29 percentage points of
  // site-name retention (44% -> 73%) for nine characters. The full name still
  // reaches Google through og:site_name below and the Organization/WebSite
  // markup in root.tsx, which is what actually populates the site name shown
  // beside the favicon in search results.
  const title =
    [
      brand ? `${productName} - ${brand} - Catalyst` : `${productName} - Catalyst`,
      brand ? `${productName} - ${brand}` : productName,
      productName,
    ].find((candidate) => candidate.length <= 60) ?? truncateOnWord(productName, 60);

  // Prefer the product's own description over a template. It is written per
  // product, already shown in the page body and already used in the JSON-LD, so
  // the meta tag was the one place ignoring it in favour of a formula. 97% of
  // products carry one; the rest fall back to the original wording.
  //
  // Absent fields are left out entirely — this template used to emit the literal
  // strings "Unknown Brand", "Unknown Subtype" and "Unknown Category" into live
  // meta tags whenever the feed omitted one.
  const descriptor = [product?.subtype, product?.category].filter(Boolean).join(' ');
  const feedDescription = (product?.product_description ?? '').trim();
  const description = feedDescription
    ? truncateToSentence(feedDescription, 155)
    : descriptor
      ? `Explore our ${descriptor} product: ${productName}${brand ? ` by ${brand}` : ''}. Discover detailed information, specifications, and benefits at Catalyst Cannabis.`
      : `Explore ${productName}${brand ? ` by ${brand}` : ''}. Discover detailed information, specifications, and benefits at Catalyst Cannabis.`;

  const canonicalUrl = `https://catalyst-cannabis.com/product/${params.slug}`;

  return [
    { title: title },
    { name: 'description', content: description },
    { tagName: 'link', rel: 'canonical', href: canonicalUrl },
    {
      name: 'robots',
      content: 'follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large',
    },
    { property: 'og:locale', content: 'en_US' },
    { property: 'og:type', content: 'product' },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:url', content: canonicalUrl },
    { property: 'og:site_name', content: siteName },
    { name: 'twitter:card', content: 'summary_large_image' },
    // The default card rather than product.image on purpose: product shots are
    // square (429x429 to 800x800 across the catalogue), so a share card either
    // slices half the height off or, under 600x315, degrades to a small
    // thumbnail. Revisit only if 1200x630 product renders ever exist.
    ...socialImageMeta(),
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
    { name: 'twitter:site', content: '@Catalyst_WFTP' },
    { name: 'twitter:creator', content: '@Catalyst_WFTP' },
    { name: 'twitter:label2', content: 'Time to read' },
    { name: 'twitter:data2', content: 'Less than a minute' },
  ];
};

// Function to get current store location data from all locations
function getCurrentStoreLocation(
  store: string,
  allLocations: WordPressLocation[],
): WordPressLocation | null {
  // Map store values to location slugs (same as in locations page)
  const storeToLocationSlug: Record<string, string> = {
    catalystantioch: 'antioch-dispensary',
    catalystbellflower: 'bellflower-dispensary',
    catalystbelmont: 'belmont-shore-dispensary',
    catalyst405: 'cherry-ave-long-beach-dispensary',
    catalystcostamesa: 'costa-mesa-dispensary',
    catalystdalycity: 'daly-city-dispensary',
    catalystpine: 'downtown-long-beach-dispensary',
    catalystelmonte: 'el-monte-dispensary',
    catalystfigueroa: 'figueroa-los-angeles-dispensary',
    catalystflorence: 'florence-los-angeles-dispensary',
    catalystgroverbeach: 'grover-beach-dispensary',
    catalysthawthorne: 'hawthorne-dispensary',
    catalysthemet: 'hemet-dispensary',
    catalystlancaster: 'lancaster-dispensary',
    catalystlift: 'retro-row-long-beach-dispensary',
    catalystlynwood: 'lynwood-dispensary',
    catalystmarina: 'marina-dispensary',
    catalystmidcity: 'mid-city-los-angeles-dispensary',
    catalystmorenovalley: 'moreno-valley-dispensary',
    catalystnormandie: 'normandie-los-angeles-dispensary',
    catalystoc3: 'oc3-santa-ana-dispensary',
    catalystriverside: 'riverside-dispensary',
    catalystsacramento: 'sacramento-dispensary',
    catalystsalinas: 'salinas-dispensary',
    catalystsantaana: 'santa-ana-dispensary',
    catalystsantabarbara: 'santa-barbara-dispensary',
    catalystsantacruz: 'santa-cruz-dispensary',
    catalystsantamaria: 'santa-maria-dispensary',
    catalystsimi: 'simi-valley-dispensary',
    catalystventura: 'ventura-dispensary',
    catalystwatsonville: 'watsonville-dispensary',
    catalystwesthollywood: 'west-hollywood-dispensary',
    catalystwestminster: 'westminster-dispensary',
  };

  const locationSlug = storeToLocationSlug[store];
  if (!locationSlug) {
    console.warn(`🚨 Store mapping missing for: ${store}`);
    return null;
  }

  const foundLocation = allLocations.find((location) => location.slug === locationSlug);
  if (!foundLocation) {
    console.warn(`🚨 WordPress location not found for slug: ${locationSlug} (store: ${store})`);
  }

  return foundLocation || null;
}

export const loader: LoaderFunction = async ({ params, request }) => {
  const { slug } = params;
  const cookieHeader = request.headers.get('cookie');
  const cookies = cookieHeader ? parse(cookieHeader) : {};
  const defaultStore =
    cookies.selectedStore || new URL(request.url).searchParams.get('store') || 'catalystbellflower';

  if (!slug) {
    throw new Response('Not Found', { status: 404 });
  }

  // Whether the visitor has actually chosen a store, as opposed to us falling
  // back to one. `defaultStore` above always has a value, so it cannot answer
  // this on its own.
  const hasStoreSelected = Boolean(cookies.selectedStore);

  let product: Product | null = null;
  let defaultAvailable = true;

  try {
    product = await getProductBySlug(slug, defaultStore, { auth: authFromRequest(request), includeUnavailable: true });
    defaultAvailable = product.inStock === true;
  } catch (error: any) {
    if (error.message !== 'Product not found') {
      return json({ error: error.message }, { status: 500 });
    }
    defaultAvailable = false;
  }

  if (!product) {
    // Not in stock at this store — but "this store" means two very different
    // things depending on whether the visitor picked it.
    //
    // The store selector is a client component (age-gate.tsx), so it writes its
    // cookie only after the visitor interacts. This loader has already run and
    // already returned by then. Redirecting on a store nobody chose would bounce
    // someone arriving from a search result or an ad off the product they
    // clicked — judged against Bellflower's shelf rather than their own — and
    // the store they then pick reloads whatever page the redirect landed on,
    // because the product URL is gone by that point.
    //
    // So when no store has been chosen, render the product and let the gate do
    // its job. The reload it fires on confirm re-runs this loader against the
    // same URL, with a real store, and takes the branch below instead.
    //
    // `allStores` asks the chain-wide question: does this product exist in stock
    // anywhere. On Typesense this was `requireStock: false`, which relaxed the
    // stock clause but still read Bellflower's collection — so a product never
    // carried at Bellflower had no document to find and fell through to the
    // redirect anyway. That was the gap `app/docs/SEO_STORE_SCOPE.md` deferred
    // to this migration; omitting `p_store` closes it.
    if (!hasStoreSelected) {
      try {
        product = await getProductBySlug(slug, defaultStore, { allStores: true });
      } catch {
        product = null;
      }
    }

    // Either the visitor has chosen a store and this is a real answer, or the
    // product has left the index entirely and there is nothing to render.
    if (!product) {
      const attributes =
        (await getProductAttributes(slug, defaultStore)) ??
        (await recoverBrandFromSlug(slug, defaultStore));

      // 302: the target is derived from stock that turns over hourly, so the
      // redirect must not be cached as permanent.
      return redirect(
        attributes ? await resolveOutOfStockTarget(attributes, defaultStore) : '/shop',
        302,
      );
    }
  }

  // Fetch all locations for nearby dispensaries
  console.log('🔍 About to fetch all locations...');
  const allLocations = await fetchAllLocations();
  console.log('📍 Fetched locations:', allLocations.length);

  // Get current store location data for FAQs and reviews
  const currentStoreLocation = getCurrentStoreLocation(defaultStore, allLocations);
  console.log('🏪 Current store location found:', !!currentStoreLocation);

  console.log(`🎯 Final state - defaultAvailable: ${defaultAvailable}, product: ${product?.name}`);
  console.log(`📋 FAQs Debug:`, {
    store: defaultStore,
    hasCurrentStoreLocation: !!currentStoreLocation,
    hasFaqs: !!currentStoreLocation?.locationDetails?.faqs,
    faqsCount: currentStoreLocation?.locationDetails?.faqs?.length || 0,
    locationTitle: currentStoreLocation?.title,
  });

  return json<LoaderData>({
    product,
    defaultAvailable,
    store: defaultStore,
    allLocations,
    currentStoreLocation,
    productUrl: new URL(request.url).origin + `/product/${product.slug}`,
  });
};

export default function ProductPage() {
  const loaderData = useLoaderData<LoaderData>();
  const {
    product,
    defaultAvailable,
    store,
    allLocations,
    currentStoreLocation,
    productUrl,
  } = loaderData;
  const [searchParams, setSearchParams] = useSearchParams();
  const [quantity, setQuantity] = useState(1);
  // Announced to screen readers when the quantity changes via +/- (see live region in the return).
  const [quantityMessage, setQuantityMessage] = useState('');

  // Shown in place of the buying controls, in both the mobile and desktop
  // columns, when the product cannot be bought at this store.
  const outOfStockNotice = (
    <div className="w-full rounded-lg bg-gray-100 p-4 text-center">
      <p className="font-bold text-red-600">
        Sorry, this product is out of stock at your selected location, please try one of our
        alternative locations below.
      </p>
    </div>
  );
  const [isLoading, setIsLoading] = useState(false);
  const [showFullLabResults, setShowFullLabResults] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [reviewRefreshKey, setReviewRefreshKey] = useState(0); // Increment to trigger ProductReviews refetch
  const [selectedImageIndex, setSelectedImageIndex] = useState(0); // Track selected image for gallery

  // Fetch reviews client-side using ReviewProvider
  const { getReviews, isLoadingReviews, fetchReviews } = useReviewCache();
  const { getBrandLogo } = useBrandLogoCache();
  const {
    hasReviewed,
    status: reviewStatus,
    isLoading: isLoadingUserReview,
    refetch: refetchUserReview,
  } = useUserReview(product?.treez_id || product?.id);

  // No need for separate refetch effect - single hook instance handles everything

  // Handler to open review modal and update URL
  const handleOpenReviewModal = () => {
    setIsReviewModalOpen(true);
    // Update URL to include review parameter for shareable link
    const newParams = new URLSearchParams(searchParams);
    newParams.set('review', 'true');
    setSearchParams(newParams, { replace: false });
  };

  // Handler to close review modal and clean up URL
  const handleCloseReviewModal = () => {
    setIsReviewModalOpen(false);
    // Mark as manually closed to prevent reopening from URL
    modalManuallyClosedRef.current = true;
    // Remove review parameter from URL when closing
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('review');
    newParams.delete('modal');
    setSearchParams(newParams, { replace: true });
  };

  // Handler to refetch both top section and bottom ProductReviews component
  const handleReviewSubmitted = () => {
    refetchUserReview(); // Update top section
    setReviewRefreshKey((prev) => prev + 1); // Trigger ProductReviews to refetch
    // Note: Modal stays open to show success message, user can close it manually
  };

  // Track if component is mounted to avoid hydration issues
  const [isMounted, setIsMounted] = useState(false);
  // Track if modal was manually closed to prevent reopening from URL
  const modalManuallyClosedRef = useRef(false);

  // Mark as mounted after hydration
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Check for review modal query parameter and sync modal state with URL
  // Handles: email links, browser back/forward buttons, manual URL changes
  useEffect(() => {
    if (!isMounted) return;

    const reviewParam = searchParams.get('review');
    const modalParam = searchParams.get('modal');
    const hasReviewInUrl = reviewParam === 'true' || modalParam === 'review';

    // Case 1: URL has review parameter but modal is closed → Open modal
    // Only open if user is logged in — otherwise trigger login flow
    if (hasReviewInUrl && !isReviewModalOpen && !modalManuallyClosedRef.current) {
      if (isLoggedIn) {
        setIsReviewModalOpen(true);
        // Normalize URL to use review=true (in case modal=review was used)
        if (reviewParam !== 'true') {
          const newParams = new URLSearchParams(searchParams);
          newParams.set('review', 'true');
          newParams.delete('modal');
          setSearchParams(newParams, { replace: true });
        }
      } else {
        // Not logged in — strip review param and trigger login
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('review');
        newParams.delete('modal');
        setSearchParams(newParams, { replace: true });
        window.dispatchEvent(new CustomEvent('login:trigger'));
      }
    }

    // Case 2: URL doesn't have review parameter but modal is open → Close modal
    // This handles browser back button and manual URL edits
    if (!hasReviewInUrl && isReviewModalOpen) {
      setIsReviewModalOpen(false);
      modalManuallyClosedRef.current = false; // Reset so modal can be reopened
    }

    // Case 3: Reset the manually closed flag when URL no longer has review parameter
    // This allows reopening the modal from a URL later
    if (!hasReviewInUrl) {
      modalManuallyClosedRef.current = false;
    }
  }, [isMounted, searchParams, setSearchParams, isReviewModalOpen, isLoggedIn]);

  // Review product ID — use treez_id (global) with fallback to product.id (store-specific)
  const reviewProductId = product?.treez_id || product?.id || '';

  useEffect(() => {
    if (isMounted && reviewProductId) {
      const cachedReviews = getReviews(reviewProductId);
      if (!cachedReviews) {
        // Use requestAnimationFrame to ensure hydration is complete before fetching
        let rafId1: number;
        let rafId2: number;
        rafId1 = requestAnimationFrame(() => {
          // Double RAF to ensure we're past hydration
          rafId2 = requestAnimationFrame(() => {
            fetchReviews(reviewProductId);
          });
        });
        return () => {
          cancelAnimationFrame(rafId1);
          if (rafId2) cancelAnimationFrame(rafId2);
        };
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted, reviewProductId]);

  // Reset selected image index when product changes
  useEffect(() => {
    setSelectedImageIndex(0);
  }, [product.id]);

  // Get all available images using proper prioritization:
  // 1. Budflow images (if any exist, use ONLY Budflow)
  // 2. Treez images (only if no Budflow)
  // 3. Brand logo (only if no product images)
  // 4. Placeholder (final fallback)
  const allAvailableImages = useMemo(() => {
    return prioritizeProductImages({
      budflow_image_url: product.budflow_image_url,
      all_images: product.all_images,
      image: product.image,
      primary_image: product.primary_image,
      brand_logo_url: product.brand_logo_url,
      brand: product.brand,
      category: product.category,
      getBrandLogo,
    });
  }, [
    product.budflow_image_url,
    product.all_images,
    product.image,
    product.primary_image,
    product.brand_logo_url,
    product.brand,
    product.category,
    getBrandLogo,
  ]);

  // Get currently displayed image based on selected index
  const currentDisplayImage = allAvailableImages[selectedImageIndex] || allAvailableImages[0];

  // Real product photo, or a brand-logo stand-in? `prioritizeProductImages`
  // only reaches its brand-logo step when every photo field was empty, so the
  // test the grid card uses answers it here too — and it has to be answered,
  // because a logo needs different treatment from a photo:
  //
  //   - `object-cover` crops and stretches a logo. A square product shot
  //     survives it; a wordmark does not.
  //   - A WHITE logo on this white figure is invisible. CAT4's is white, which
  //     is why `LIGHT_BRAND_LOGOS` exists, and the grid card already renders it
  //     on a grey tile. This page did not, so 29 CAT4 products at Bellflower
  //     showed a card image and then an apparently empty product page.
  const hasRealPhoto = !!(
    product.primary_image ||
    product.image ||
    product.budflow_image_url ||
    (product.all_images && product.all_images.length > 0)
  );
  const showingLightLogo = !hasRealPhoto && isLightBrandLogo(product.brand);

  const reviews = getReviews(reviewProductId) || [];

  // Calculate average rating from reviews
  const averageRating = useMemo(() => {
    if (reviews.length === 0) return 0;
    const sum = reviews.reduce((acc, review) => acc + review.rating, 0);
    return Math.round((sum / reviews.length) * 10) / 10; // Round to 1 decimal place
  }, [reviews]);

  // Function to scroll to reviews section
  const scrollToReviews = () => {
    const reviewsElement = document.getElementById('product-reviews');
    if (reviewsElement) {
      const elementPosition = reviewsElement.offsetTop;
      const offsetPosition = elementPosition - 20; // 20px padding above

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  // Helper function to render star rating
  const renderStarRating = (rating: number, size: 'sm' | 'lg' = 'lg') => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    const starSize = size === 'lg' ? 'h-[18px] w-[17.87px]' : 'h-4 w-4';

    return (
      <div className="flex items-center gap-1 px-1">
        {/* Full stars */}
        {[...Array(fullStars)].map((_, i) => (
          <img key={`full-${i}`} src="/icons/gold-leaf.svg" alt="" className={starSize} />
        ))}

        {/* Half star */}
        {hasHalfStar && <img src="/icons/gold-leaf-half.svg" alt="" className={starSize} />}

        {/* Empty stars */}
        {[...Array(emptyStars)].map((_, i) => (
          <img
            key={`empty-${i}`}
            src="/icons/gold-leaf.svg"
            alt=""
            className={`${starSize} opacity-30`}
          />
        ))}
      </div>
    );
  };

  // Sticky functionality for image (left) and price (right)
  // Only enable sticky on desktop (lg breakpoint) when images are in their own column
  const { containerRef: imageContainerRef, stickyRef: imageStickyRef } = useStickyOptimized({
    side: 'left',
    minScreenWidth: 1024, // Only enable on desktop (lg breakpoint)
  });

  const { containerRef: priceContainerRef, stickyRef: priceStickyRef } = useStickyOptimized({
    side: 'right',
  });

  // Check login status on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const getCookie = (name: string): string | null => {
        const value = '; ' + document.cookie;
        const parts = value.split('; ' + name + '=');
        if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
        return null;
      };
      const userId = getCookie('catalystUserId');
      setIsLoggedIn(!!userId);
    }
  }, []);

  // Handle variant change - navigate to new variant product page
  const handleVariantChange = async (variantId: string) => {
    try {
      // Get the current selected store
      const selectedStore = localStorage.getItem('selectedStore') || store;

      // Use existing API route to get the product data (which includes slug)
      const response = await fetch(`/api/product?productId=${variantId}&store=${selectedStore}`);

      if (!response.ok) {
        throw new Error('Failed to get product data');
      }

      const productData = await response.json();

      if (productData.error) {
        throw new Error(productData.error);
      }

      // Navigate to the new variant product page using slug
      navigate(`/product/${productData.slug}`);
    } catch (error) {
      console.error('Error getting variant product:', error);
      toast.error('Unable to load product variant');
    }
  };

  // Parse lab results from the Treez lab_results JSON array
  const parseLabResults = (labResultsString: string) => {
    if (!labResultsString || typeof labResultsString !== 'string' || !labResultsString.trim()) {
      return [];
    }
    try {
      const resultsArray = JSON.parse(labResultsString);
      return Array.isArray(resultsArray) ? resultsArray : [];
    } catch (error) {
      console.error('Error parsing lab results:', error);
      return [];
    }
  };

  // Process all lab results from Treez data
  const processLabResults = (resultsArray: any[]) => {
    const processedResults: { [key: string]: string } = {};

    resultsArray.forEach((result) => {
      const { amount, amount_type, result_type } = result;

      if (result_type && amount && amount.minimum_value) {
        const formattedAmount =
          amount_type === 'PERCENTAGE'
            ? `${parseFloat(amount.minimum_value).toFixed(2)}%`
            : `${parseFloat(amount.minimum_value).toFixed(2)}${amount_type || ''}`;

        // Normalize result type names for display
        let displayName = result_type;
        if (result_type === 'DELTA-9_THC' || result_type === 'THCA') {
          displayName = 'THC';
        } else if (result_type === 'CBDA') {
          displayName = 'CBD';
        }

        processedResults[displayName] = formattedAmount;
      }
    });

    return processedResults;
  };

  const rawLabResults = parseLabResults(product.lab_results || '[]');
  const allLabResults = processLabResults(rawLabResults);

  // Helper function to check if a value is meaningful (not 0 or empty)
  const isMeaningfulValue = (value: string | null | undefined): boolean => {
    if (!value) return false;
    const trimmed = value.toString().trim();
    if (trimmed === '' || trimmed === '0' || trimmed === '0%' || trimmed === '0.00%') return false;
    const numericValue = parseFloat(trimmed.replace('%', ''));
    return !isNaN(numericValue) && numericValue > 0;
  };

  // Primary results (only show THC and CBD if they have meaningful values)
  const primaryResults = [
    ...(isMeaningfulValue(allLabResults.THC || product.thc)
      ? [{ label: 'THC', value: allLabResults.THC || product.thc }]
      : []),
    ...(isMeaningfulValue(allLabResults.CBD || product.cbd)
      ? [{ label: 'CBD', value: allLabResults.CBD || product.cbd }]
      : []),
  ].filter(Boolean);

  // Additional lab results (everything except THC and CBD)
  const additionalResults = Object.entries(allLabResults)
    .filter(([key]) => !['THC', 'CBD'].includes(key))
    .map(([key, value]) => ({
      label: key,
      value: String(value),
    }))
    .sort((a, b) => a.label.localeCompare(b.label)); // Sort alphabetically

  // TODO: BACKEND TEAM - Add more comprehensive lab results to Treez integration
  // Currently only THC and CBD are available. The system is ready to display:
  // - CBN, CBG, CBC, CBDV, THCV (other cannabinoids)
  // - Terpenes (Limonene, Myrcene, Pinene, etc.)
  // - Any other compounds from lab testing
  // The dropdown will automatically appear when additional lab results are available
  // in the product.lab_results JSON array from Treez

  // 🔥 GA4 Enhanced Ecommerce - view_item event
  useEffect(() => {
    if (product) {
      // 🔥 Enhanced Analytics - Both GA4 + Klaviyo tracking
      analytics.trackViewItemComplete({
        id: product.id.toString(),
        name: product.name,
        category: product.category,
        brand: product.brand,
        price: product.salePrice || product.price,
      });
    }
  }, [product]);
  const { openCart, incrementCount } = useCart();
  const navigate = useNavigate();

  const incrementQuantity = () => {
    const next = quantity + 1;
    setQuantity(next);
    setQuantityMessage(`Quantity: ${next}`);
  };
  const decrementQuantity = () => {
    if (quantity > 1) {
      const next = quantity - 1;
      setQuantity(next);
      setQuantityMessage(`Quantity: ${next}`);
    }
  };

  // Clear the announcement shortly after so it isn't re-read while navigating the page.
  useEffect(() => {
    if (!quantityMessage) return;
    const t = setTimeout(() => setQuantityMessage(''), 1000);
    return () => clearTimeout(t);
  }, [quantityMessage]);

  const handleAddToCart = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!defaultAvailable) return;
    setIsLoading(true);
    // Optimistic add — reflect the item in the side-cart instantly (matches the
    // grid/modal cards). Image prefers budflow_image_url (matches the cart).
    window.dispatchEvent(
      new CustomEvent('cart:optimistic-add', {
        detail: {
          name: product.name,
          name_sanitized: (product as any).name_sanitized || product.name,
          brand: product.brand,
          category: (product as any).category ?? (product as any).category_type,
          price: product.salePrice ?? product.price,
          price_sell: product.price,
          primary_image:
            (product as any).budflow_image_url ||
            product.primary_image ||
            (product as any).image ||
            '',
          onsale: product.salePrice != null && product.salePrice < product.price,
          quantity,
        },
      }),
    );
    try {
      const selectedStore = localStorage.getItem('selectedStore');
      const order_id = selectedStore
        ? localStorage.getItem(`token_${selectedStore}`) || 'default_order_id'
        : 'default_order_id';

      const payload = {
        actionType: 'ADD',
        order_id,
        product_id: product.id,
        quantity: String(quantity),
      };

      const response = await cartFetch('/api/cart', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const responseData = await response.clone().json();

      // Check for errors in response body (API returns 200 with errors)
      if (responseData?.error && response.ok) {
        const parsedError = parseCartError(responseData, response);
        if (parsedError.shouldShowToast) {
          toast.error(parsedError.message);
        }
        throw new Error(parsedError.message);
      }

      if (!response.ok) {
        const parsedError = parseCartError(responseData, response);
        if (parsedError.shouldShowToast) {
          toast.error(parsedError.message);
        }
        throw new Error(parsedError.message);
      }
      await response.json();
      incrementCount(quantity);

      // 🔥 Enhanced Analytics - Both GA4 + Klaviyo tracking
      analytics.trackAddToCartComplete({
        id: product.id.toString(),
        name: product.name,
        category: product.category,
        brand: product.brand,
        price: product.salePrice || product.price,
        quantity: quantity,
      });

      openCart();
    } catch (error: any) {
      console.error('Error adding to cart:', error);
      // Only show generic toast if error wasn't already handled
      if (!isErrorAlreadyHandled(error)) {
        toast.error('Failed to add item to cart. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Format prices for display (with $ symbol)
  const formattedPrice = product.price != null ? `$${product.price.toFixed(2)}` : 'N/A';
  const formattedSalePrice = product.salePrice != null ? `$${product.salePrice.toFixed(2)}` : null;

  // Numeric prices for Schema.org (without $ symbol)
  const numericPrice = product.price != null ? product.price.toFixed(2) : null;
  const numericSalePrice = product.salePrice != null ? product.salePrice.toFixed(2) : null;

  // Memoized discount calculation for performance
  const discountPercentage = useMemo(() => {
    return product.salePrice && product.salePrice < product.price
      ? Math.round(((product.price - product.salePrice) / product.price) * 100)
      : null;
  }, [product.price, product.salePrice]);

  return (
    <>
      {/* Shared live region: announces quantity changes from either stepper (desktop/mobile). */}
      <span className="sr-only" aria-live="polite" aria-atomic="true">
        {quantityMessage}
      </span>
      <div className="mx-auto w-full max-w-[108rem]">
        {/* Breadcrumbs Navigation */}
        <nav aria-label="Breadcrumb" className="w-full px-4 py-4 md:px-6">
          <div className="mx-auto max-w-[100rem]">
            <ol className="flex items-center gap-2.5">
              {/* Home */}
              <li className="flex items-center gap-2.5">
                <Link
                  to="/"
                  className="flex min-h-[24px] min-w-[24px] items-center justify-center rounded text-[#264B9A] hover:underline focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  aria-label="Go to homepage"
                >
                  <img
                    src="/icons/home-icon.svg"
                    alt="Home navigation"
                    aria-hidden="true"
                    className="h-4 w-4"
                  />
                </Link>
                <span className="text-xs font-light text-[#8D86B8]" aria-hidden="true">
                  /
                </span>
              </li>

              {/* Shop */}
              <li className="flex items-center gap-2.5">
                <Link
                  to="/shop"
                  className="rounded text-sm font-medium text-[#264B9A] hover:underline focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Shop
                </Link>
                <span className="text-xs font-light text-[#8D86B8]" aria-hidden="true">
                  /
                </span>
              </li>

              {/*
                Category — via categoryPath, not `category.toLowerCase()`. The
                raw value gives /category/cartridge, a self-canonicalising
                duplicate of /category/vapes, so the breadcrumb was pointing at
                the copy rather than the page meant to rank.
              */}
              {product.category && categoryPath(product.category) && (
                <li className="flex items-center gap-2.5">
                  <Link
                    to={categoryPath(product.category)!}
                    className="rounded text-sm font-medium text-[#264B9A] hover:underline focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    {product.category
                      .split(' ')
                      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                      .join(' ')}
                  </Link>
                  <span className="text-xs font-light text-[#8D86B8]" aria-hidden="true">
                    /
                  </span>
                </li>
              )}

              {/* Current Product */}
              <li>
                {/* Truncated name for small screens only */}
                <span className="text-sm font-light text-[#1E293B] md:hidden" aria-current="page">
                  {(() => {
                    const titleCaseName = toTitleCase(product.name);

                    const maxLength = 16;
                    return titleCaseName.length > maxLength
                      ? `${titleCaseName.substring(0, maxLength).trim()}...`
                      : titleCaseName;
                  })()}
                </span>

                {/* Full name for md screens and above */}
                <span
                  className="hidden text-sm font-light text-[#1E293B] md:inline"
                  aria-current="page"
                >
                  {toTitleCase(product.name)}
                </span>
              </li>
            </ol>
          </div>
        </nav>
      </div>

      {/* Schema.org Microdata Wrapper */}
      <div itemScope itemType="https://schema.org/Product" id="main-content">
        <section className="product-card font-futura relative mx-auto flex w-full max-w-[100rem] flex-col bg-white px-4 lg:p-6">
          {/* Product Name - Mobile Only */}
          <header className="mb-2 lg:hidden">
            <h1
              className="font-poppins text-2xl leading-8 font-semibold text-[#0F172A] normal-case"
              itemProp="name"
            >
              {toTitleCase(product.name) || 'N/A'}
            </h1>

            {/* Reviews Section - Only show button for logged-in users */}
            {/* Write Review Button - Mobile, always reserve space to prevent CLS */}
            <div className={`min-h-[28px] ${!isLoggedIn ? 'invisible' : ''}`}>
              {/* Show status message only if we have confirmed review (not loading) */}
              {!isLoadingUserReview && hasReviewed && reviewStatus !== 'rejected' ? (
                <div className="font-helvetica-neue text-base leading-5 text-[#065F46]">
                  {reviewStatus === 'pending' ? (
                    <span className="font-medium">Thank You! Review pending approval</span>
                  ) : (
                    <span className="font-medium">Thank you for your review!</span>
                  )}
                </div>
              ) : !isLoadingUserReview ? (
                /* Only show button after checking - prevents duplicate reviews after hard refresh */
                <button
                  type="button"
                  onClick={handleOpenReviewModal}
                  className="font-helvetica-neue inline-flex items-center gap-2 text-base leading-5 font-medium text-[#264B9A] underline transition-colors hover:text-[#1e3a7a] focus:ring-2 focus:ring-[#ffd100] focus:ring-offset-2 focus:outline-none"
                  aria-label={`Leave a review for ${product.name}`}
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                    />
                  </svg>
                  Leave a Review
                </button>
              ) : (
                /* Loading placeholder to reserve space and prevent CLS */
                <div className="font-helvetica-neue text-base leading-5 text-transparent">
                  <span className="invisible">Leave a Review</span>
                </div>
              )}
            </div>

            {/* Star Rating - Mobile Only - Always reserve space */}
            <div className="flex w-full items-center justify-between">
              <div className={`flex items-center gap-1 ${reviews.length === 0 ? 'invisible' : ''}`}>
                <span className="font-helvetica-neue flex items-center text-xl leading-[18px] text-[#181818]">
                  {reviews.length > 0 ? averageRating.toFixed(1) : '0.0'}
                </span>
                <div className="flex items-center gap-1">
                  {renderStarRating(reviews.length > 0 ? averageRating : 0)}
                  <button
                    onClick={scrollToReviews}
                    className="font-helvetica-neue cursor-pointer text-xs leading-[18px] font-normal text-gray-600 transition-colors hover:text-[#264B9A] hover:underline focus:ring-2 focus:ring-[#ffd100] focus:ring-offset-1 focus:outline-none"
                    aria-label={`View ${reviews.length} customer review${reviews.length !== 1 ? 's' : ''}`}
                  >
                    ({reviews.length} review{reviews.length !== 1 ? 's' : ''})
                  </button>
                </div>
              </div>

              <div className="flex items-center">
                <span className="font-helvetica-neue text-xs leading-4 font-normal text-black">
                  {formatProductSize(product.size) || 'N/A'}
                </span>
              </div>
            </div>
          </header>

          <div className="mb-6 flex flex-col lg:flex-row lg:gap-16" ref={imageContainerRef}>
            {/* Left Column - Product Image */}
            <div className="w-full lg:w-[35%]">
              <div className="mx-auto w-full max-w-[31.25rem]" ref={imageStickyRef}>
                <div key={product.id}>
                  <figure
                    className={`relative aspect-square w-full max-w-[31.25rem] rounded-md border border-black/10 ${
                      showingLightLogo ? `${LIGHT_LOGO_TILE} p-8` : ''
                    }`}
                    itemProp="image"
                  >
                    {discountPercentage !== null && (
                      <span className="sale absolute top-3 left-3 z-10 rounded bg-[#ffd100] px-2 py-2 text-xs font-bold shadow-sm">
                        {discountPercentage}% OFF
                      </span>
                    )}

                    <img
                      src={currentDisplayImage}
                      alt={product.name || 'N/A'}
                      className={`h-full w-full rounded-md ${
                        hasRealPhoto ? 'object-cover' : 'object-contain'
                      }`}
                      itemProp="image"
                      loading="eager"
                      decoding="sync"
                      data-fallback-attempted="false"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        const hasAttemptedFallback =
                          target.getAttribute('data-fallback-attempted') === 'true';

                        // Prevent infinite error loops - only try fallback once
                        if (!hasAttemptedFallback) {
                          target.setAttribute('data-fallback-attempted', 'true');
                          // Use same fallback logic as prioritizeProductImages: getFillerImage
                          const fallbackImage = getFillerImage(
                            product.brand,
                            product.category,
                            getBrandLogo(product.brand),
                          );
                          target.src = fallbackImage;
                          target.alt = 'Product image - Failed to load';
                          // A logo is not a photo: stop cropping it, and keep a
                          // white one visible. Mirrors the static branch above,
                          // which cannot know a URL was going to 404.
                          target.classList.remove('object-cover');
                          target.classList.add('object-contain');
                          if (isLightBrandLogo(product.brand)) {
                            target.parentElement?.classList.add(
                              ...LIGHT_LOGO_TILE.split(' '),
                              'p-8',
                            );
                          }
                        } else {
                          // The default placeholder itself failed — show nothing
                          // rather than a broken-image icon.
                          target.style.display = 'none';
                        }
                      }}
                    />
                  </figure>

                  {/* Thumbnail Gallery - Shown when product has multiple images (Mobile + Desktop) */}
                  {allAvailableImages.length > 1 && (
                    <div
                      className={`mt-2.5 w-full gap-2.5 pb-3 md:flex md:pb-0 ${
                        allAvailableImages.length > 5
                          ? 'overflow-x-auto scroll-smooth [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden md:[scrollbar-width:thin] md:[scrollbar-color:#d1d5db_transparent] md:[&::-webkit-scrollbar]:block md:[&::-webkit-scrollbar]:h-2 md:[&::-webkit-scrollbar-thumb]:rounded-full md:[&::-webkit-scrollbar-thumb]:bg-gray-300 md:hover:[&::-webkit-scrollbar-thumb]:bg-gray-400 md:[&::-webkit-scrollbar-track]:bg-transparent'
                          : 'flex'
                      } ${
                        // Mobile: Always enable horizontal scroll with touch-friendly behavior, hide scrollbar
                        'flex snap-x snap-mandatory overflow-x-auto [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden'
                      }`}
                      aria-label="Product image gallery"
                      role="region"
                      aria-roledescription="carousel"
                    >
                      {allAvailableImages.map((image, index) => (
                        <button
                          key={index}
                          onClick={() => setSelectedImageIndex(index)}
                          className={`flex flex-shrink-0 items-center justify-center rounded-md bg-white transition-all hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 ${
                            selectedImageIndex === index
                              ? 'border-2 border-[#264B9A]'
                              : 'border border-black/10'
                          } ${
                            // Mobile: Smaller size (80x80px for touch-friendly 44px+ targets)
                            // Desktop: Larger size (110x110px)
                            'h-20 min-h-[80px] w-20 min-w-[80px] snap-start md:h-[110px] md:min-h-[110px] md:w-[110px] md:min-w-[110px]'
                          }`}
                          aria-label={`View product image ${index + 1} of ${allAvailableImages.length}`}
                          aria-pressed={selectedImageIndex === index}
                          aria-current={selectedImageIndex === index ? 'true' : 'false'}
                          type="button"
                        >
                          <img
                            src={image}
                            alt={`Product view ${index + 1}`}
                            className="h-auto max-h-[50px] w-fit object-scale-down md:max-h-[70px]"
                            loading={index < 3 ? 'eager' : 'lazy'}
                            decoding="async"
                            data-fallback-attempted="false"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              const hasAttemptedFallback = target.getAttribute('data-fallback-attempted') === 'true';
                              
                              // Prevent infinite error loops - only try fallback once
                              if (!hasAttemptedFallback) {
                                target.setAttribute('data-fallback-attempted', 'true');
                                // Use same fallback logic as prioritizeProductImages: getFillerImage
                                const fallbackImage = getFillerImage(product.brand, product.category, getBrandLogo(product.brand));
                                target.src = fallbackImage;
                                target.alt = 'Product image - Failed to load';
                              } else {
                                // If fallback also failed, hide the image to prevent broken image icon
                                target.style.display = 'none';
                              }
                            }}
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Mobile Price Section - Under image on mobile only */}
            <div className="mx-auto mt-6 w-full max-w-[31.25rem] lg:hidden">
              <div className="flex w-full flex-col items-start gap-5 bg-white py-3">
                {/* Price & Quantity Row */}
                <div className="flex w-full items-start justify-center gap-4 px-11">
                  {/* Price Section - Left side */}
                  <div className="flex flex-1 flex-col items-center justify-center gap-5">
                    <div className="flex flex-col items-center justify-end gap-2.5">
                      <span className="font-helvetica-neue text-4xl leading-6 font-bold text-[#181818]">
                        ${(product.salePrice || product.price).toFixed(2)}
                      </span>
                      {product.salePrice && (
                        <span className="font-helvetica-neue text-lg leading-6 font-medium text-[#4A5565] line-through">
                          ${product.price.toFixed(2)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Quantity Controls - Right side */}
                  {defaultAvailable && (
                    <div className="flex flex-1 items-start justify-center gap-7 rounded-lg pt-2">
                      <button
                        onClick={decrementQuantity}
                        className="font-poppins flex h-6 w-6 items-center justify-center text-center text-3xl text-[#264B9A] hover:bg-[#264B9A] hover:text-white"
                        aria-label="Decrease quantity"
                      >
                        -
                      </button>
                      <span className="font-poppins text-[22px] leading-6 font-bold text-[#264B9A]">
                        {quantity}
                      </span>
                      <button
                        onClick={incrementQuantity}
                        className="font-poppins flex h-6 w-6 items-center justify-center text-center text-3xl text-[#264B9A] hover:bg-[#264B9A] hover:text-white"
                        aria-label="Increase quantity"
                      >
                        +
                      </button>
                    </div>
                  )}
                </div>

                {/* Add to Cart Button - Full width at bottom.
                    Gated on defaultAvailable exactly as the desktop column is: this
                    block used to offer a working Add To Cart on a product the desktop
                    layout was refusing to sell, which went unnoticed while an
                    out-of-stock PDP always redirected before it could render. */}
                {defaultAvailable ? (
                  <button
                    onClick={handleAddToCart}
                    disabled={isLoading}
                    className="font-helvetica-neue flex h-12 w-full items-center justify-center gap-2 rounded-lg border-2 border-[#264B9A] bg-[#264B9A] px-2 py-4 text-base leading-4 font-medium text-white hover:bg-[#1e3a7a] disabled:opacity-50"
                  >
                    {isLoading ? 'Adding...' : 'Add To Cart'}
                  </button>
                ) : (
                  outOfStockNotice
                )}
              </div>
            </div>

            {/* Middle Column - Product Details */}
            <div className="mt-2 flex flex-col md:mt-4 lg:mt-0 lg:w-[40%]">
              {/* Product Header */}
              <div className="flex flex-col items-start">
                {/* Product Name - Desktop Only */}
                <header className="hidden w-full lg:block">
                  {/* Deliberately not a second <h1>. The mobile header above is the
                      page's one real heading; this is the same text laid out for wide
                      screens, and only ever one of the two is displayed. Rendering both
                      as <h1> put two of them in the source on all 1,079 product pages,
                      and gave the Product schema two itemProp="name" values.
                      role/aria-level keep it a level-1 heading for screen readers on
                      desktop, where the mobile one is display:none and unreadable. */}
                  <p
                    role="heading"
                    aria-level={1}
                    className="font-poppins text-[32px] leading-10 font-semibold text-[#0F172A] normal-case"
                  >
                    {toTitleCase(product.name) || 'N/A'}
                  </p>
                </header>

                {/* Reviews Section - Only show button for logged-in users */}
                {/* Write Review Button - Desktop, always reserve space to prevent CLS */}
                <div className={`hidden min-h-[28px] lg:block ${!isLoggedIn ? 'invisible' : ''}`}>
                  {/* Show status message only if we have confirmed review (not loading) */}
                  {!isLoadingUserReview && hasReviewed && reviewStatus !== 'rejected' ? (
                    <div className="font-helvetica-neue mt-2 text-base leading-5 text-[#065F46]">
                      {reviewStatus === 'pending' ? (
                        <span className="font-medium">Thank You! Review pending approval</span>
                      ) : (
                        <span className="font-medium">Thank you for your review!</span>
                      )}
                    </div>
                  ) : !isLoadingUserReview ? (
                    /* Only show button after checking - prevents duplicate reviews after hard refresh */
                    <button
                      type="button"
                      onClick={handleOpenReviewModal}
                      className="font-helvetica-neue mt-2 inline-flex items-center gap-2 text-base leading-5 font-medium text-[#264B9A] underline transition-colors hover:text-[#1e3a7a] focus:ring-2 focus:ring-[#ffd100] focus:ring-offset-2 focus:outline-none"
                      aria-label={`Leave a review for ${product.name}`}
                    >
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                        />
                      </svg>
                      Leave a Review
                    </button>
                  ) : (
                    /* Loading placeholder to reserve space and prevent CLS */
                    <div className="font-helvetica-neue mt-2 text-base leading-5 text-transparent">
                      <span className="invisible">Leave a Review</span>
                    </div>
                  )}
                </div>

                {/* Star Rating - Desktop Only - Always reserve space */}
                <div
                  className={`hidden w-full items-center gap-1 lg:flex ${reviews.length === 0 ? 'invisible' : ''}`}
                >
                  <span className="font-helvetica-neue flex items-center text-xl leading-[18px] text-[#181818]">
                    {reviews.length > 0 ? averageRating.toFixed(1) : '0.0'}
                  </span>
                  <div className="flex items-center gap-1">
                    {renderStarRating(reviews.length > 0 ? averageRating : 0)}
                    <button
                      onClick={scrollToReviews}
                      className="font-helvetica-neue cursor-pointer text-xs leading-[18px] font-normal text-gray-600 transition-colors hover:text-[#264B9A] hover:underline focus:ring-2 focus:ring-[#ffd100] focus:ring-offset-1 focus:outline-none"
                      aria-label={`View ${reviews.length} customer review${reviews.length !== 1 ? 's' : ''}`}
                    >
                      ({reviews.length} review{reviews.length !== 1 ? 's' : ''})
                    </button>
                  </div>
                </div>
              </div>

              {/* Product Tags — each links to the page listing more like it */}
              <div className="mb-6 flex flex-col items-start gap-3 pt-3">
                <ProductTaxonomyLinks
                  brand={product.brand}
                  category={product.category}
                  subtype={product.subtype}
                  labelClassName="text-[#6B7280]"
                />
              </div>

              {/* Product Variants Section */}
              {product.budflow_flavors && (
                <ProductVariants
                  currentProductId={product.id}
                  currentProductName={product.name}
                  budflowFlavors={product.budflow_flavors}
                  onVariantChange={handleVariantChange}
                />
              )}

              {/* Product Description Accordion */}
              {product.product_description && (
                <div className="mb-6 flex flex-col items-start gap-[14px] bg-white">
                  <div className="flex w-full items-center gap-8 pr-2">
                    <h2 className="font-helvetica-neue text-xl leading-6 font-medium text-black">
                      Product Description
                    </h2>
                  </div>
                  <div className="flex w-full flex-col items-center gap-[6px]">
                    <div className="flex w-full flex-col items-start gap-[14px] pb-2.5">
                      <div
                        className="font-helvetica-neue w-full text-base leading-6 font-light tracking-[0.02em] text-black"
                        itemProp="description"
                      >
                        {product.product_description}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Disposal Notice - vape products only */}
              {isVapeProduct(product) && <DisposalNotice />}

              {/* Lab Results Accordion */}
              <div className="mb-6 flex flex-col items-start gap-4 bg-white pt-6">
                <div className="flex w-full flex-col gap-2">
                  <div className="flex w-full items-center gap-8 pr-2">
                    <h2 className="font-helvetica-neue text-center text-xl leading-6 font-medium text-black">
                      Lab Results
                    </h2>
                  </div>
                  {/* Batch Variation Disclaimer */}
                  <p className="font-helvetica-neue text-sm leading-5 font-normal text-[#6B7280] italic">
                    Lab results may vary by batch. Actual product may differ from displayed values.
                  </p>
                </div>
                <div className="flex w-full flex-col items-start gap-[6px]">
                  <div className="flex w-full flex-col items-start pb-3">
                    {/* Primary Lab Results - THC and CBD */}
                    <div className="flex w-full">
                      {primaryResults.map((result, index) => (
                        <div
                          key={result.label}
                          className={`flex flex-1 items-start gap-2.5 p-2 ${index % 2 === 0 ? 'bg-[#F4F4F4]' : 'bg-white'}`}
                        >
                          <span className="font-helvetica-neue text-sm leading-[17px] font-medium text-black">
                            {result.label}:
                          </span>
                          <span className="font-helvetica-neue flex-1 text-sm leading-[17px] font-normal text-black">
                            {result.value}
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* Additional Lab Results - Shown when expanded */}
                    {showFullLabResults && additionalResults.length > 0 && (
                      <div className="mt-2 flex w-full flex-col">
                        {additionalResults.map((result, index) => (
                          <div
                            key={result.label}
                            className={`flex w-full items-start gap-2.5 p-2 ${index % 2 === 0 ? 'bg-[#F4F4F4]' : 'bg-white'}`}
                          >
                            <span className="font-helvetica-neue text-sm leading-[17px] font-medium text-black">
                              {result.label}:
                            </span>
                            <span className="font-helvetica-neue flex-1 text-sm leading-[17px] font-normal text-black">
                              {result.value}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Toggle Button - Only show if there are additional results */}
                  {additionalResults.length > 0 && (
                    <button
                      onClick={() => setShowFullLabResults(!showFullLabResults)}
                      className="flex w-full items-center justify-center gap-2 bg-white py-1 pr-2 transition-colors hover:bg-gray-50"
                    >
                      <span className="font-helvetica-neue text-base leading-5 font-medium text-[#264B9A]">
                        {showFullLabResults ? 'Show Less' : 'Read Full List'}
                      </span>
                      <div className="h-[14px] w-[14px]">
                        {showFullLabResults ? (
                          // Chevron Up
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path
                              d="M3.5 8.75L7 5.25L10.5 8.75"
                              stroke="#264B9A"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        ) : (
                          // Chevron Down
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path
                              d="M3.5 5.25L7 8.75L10.5 5.25"
                              stroke="#264B9A"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </div>
                    </button>
                  )}
                </div>
                <div className="h-0.5 w-full bg-[#EBEBE6]"></div>
              </div>

              {/* Product Reviews Section */}
              <div className="mb-6 w-full">
                <ProductReviews
                  productId={reviewProductId}
                  onWriteReviewClick={handleOpenReviewModal}
                  isLoggedIn={isLoggedIn}
                  refreshKey={reviewRefreshKey}
                  hasReviewed={hasReviewed}
                  reviewStatus={reviewStatus}
                  isLoadingUserReview={isLoadingUserReview}
                />
              </div>

              {/* Store FAQs Section */}
              {currentStoreLocation?.locationDetails?.faqs &&
                currentStoreLocation.locationDetails.faqs.length > 0 && (
                  <div className="mb-6 w-full">
                    <div className="w-full [&>section]:w-full [&>section>div]:w-full [&>section>div]:max-w-none">
                      <FaqsSection
                        faqs={currentStoreLocation.locationDetails.faqs}
                        locationName={currentStoreLocation.title}
                        showLocationSpecificContent={false}
                      />
                    </div>
                  </div>
                )}
            </div>

            {/* Right Column - Price & Purchase - Desktop only */}
            <div className="hidden lg:block lg:w-[25%]" ref={priceContainerRef}>
              <div
                className="mx-auto flex w-full max-w-[310px] flex-col items-center gap-4 rounded-lg bg-white p-6 shadow-[0px_2px_6px_rgba(0,0,0,0.15)]"
                ref={priceStickyRef}
              >
                {/* Price Section */}
                <div
                  className="flex w-full flex-col items-center justify-center gap-5"
                  itemProp="offers"
                  itemScope
                  itemType="https://schema.org/Offer"
                >
                  <meta itemProp="priceCurrency" content="USD" />
                  {numericSalePrice ? (
                    <meta itemProp="price" content={numericSalePrice} />
                  ) : numericPrice ? (
                    <meta itemProp="price" content={numericPrice} />
                  ) : null}
                  <div className="font-helvetica-neue flex flex-row items-end justify-center gap-2.5">
                    {formattedSalePrice ? (
                      <>
                        <span className="font-helvetica-neue text-4xl leading-6 font-bold text-[#181818]">
                          {formattedSalePrice}
                        </span>
                        <span className="font-helvetica-neue text-lg leading-6 font-medium text-[#4A5565] line-through">
                          {formattedPrice}
                        </span>
                      </>
                    ) : (
                      <span className="font-helvetica-neue text-4xl leading-6 font-bold text-[#181818]">
                        {formattedPrice}
                      </span>
                    )}
                  </div>
                  <meta itemProp="availability" content={defaultAvailable ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock'} />
                </div>

                {/* Quantity Info */}
                <div className="flex w-full items-center justify-center">
                  <span className="text-xs leading-4 font-normal text-black">
                    {formatProductSize(product.size) || 'N/A'}
                  </span>
                </div>

                {/* Low Stock Alert */}
                <LowStockAlert
                  stock={product.stock}
                  sellableQuantity={product.sellable_quantity}
                  className="w-full"
                />

                {/* Buttons Section */}
                {defaultAvailable ? (
                  <div className="flex w-full flex-col items-center gap-4">
                    {/* Quantity Controls */}
                    <div className="flex h-12 w-full items-center justify-center gap-7 rounded-lg p-4">
                      <button
                        onClick={decrementQuantity}
                        className="font-poppins flex h-6 w-6 items-center justify-center text-center text-3xl text-[#264B9A] hover:bg-[#264B9A] hover:text-white"
                        aria-label="Decrease quantity"
                      >
                        -
                      </button>
                      <span className="font-poppins text-[22px] leading-6 font-bold text-[#264B9A]">
                        {quantity}
                      </span>
                      <button
                        onClick={incrementQuantity}
                        className="font-poppins flex h-6 w-6 items-center justify-center text-center text-3xl text-[#264B9A] hover:bg-[#264B9A] hover:text-white"
                        aria-label="Increase quantity"
                      >
                        +
                      </button>
                    </div>

                    {/* Add to Cart Button */}
                    <button
                      onClick={handleAddToCart}
                      aria-label={`Add ${quantity} ${quantity === 1 ? 'item' : 'items'} of ${product.name} to cart`}
                      className="relative flex h-12 w-full items-center justify-center gap-2 rounded-lg border-2 border-[#264B9A] bg-[#264B9A] p-4 text-base leading-4 font-medium text-white before:absolute before:inset-0 before:min-h-[44px] before:min-w-[44px] before:content-[''] hover:bg-[#1e3a7a] focus:ring-2 focus:ring-[#ffd100] focus:ring-offset-2 focus:outline-none"
                    >
                      {isLoading ? 'Adding...' : 'Add To Cart'}
                    </button>
                  </div>
                ) : (
                  outOfStockNotice
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Structured Data (JSON-LD) */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Product',
              name: product.name,
              image:
                product.image ||
                getFillerImage(product.brand, product.category, getBrandLogo(product.brand)),
              description: product.product_description || '',
              sku: product.id,
              brand: {
                '@type': 'Brand',
                name: product.brand || 'Unknown',
              },
              offers: {
                '@type': 'Offer',
                url: productUrl,
                priceCurrency: 'USD',
                price: product.salePrice ?? product.price,
                availability: defaultAvailable ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
              },
            }),
          }}
        ></script>

        {/* Shop Nearby Dispensaries Section */}
        <ShopNearbyDispensaries currentStore={store} allLocations={allLocations} />

        {/* Product Review Modal - Always render (handles nested modal detection internally) */}
        <ProductReviewModal
          isOpen={isReviewModalOpen}
          onClose={handleCloseReviewModal}
          product={{
            id: product.id,
            treezId: product.treez_id,
            name: product.name || 'Product',
            image:
              product.image ||
              getFillerImage(product.brand, product.category, getBrandLogo(product.brand)),
            brand: product.brand,
          }}
          onReviewSubmitted={handleReviewSubmitted}
        />
      </div>
    </>
  );
}
